import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Client, CommandStatus, FirmwareUpdateCommandStatusDataOut, PermissionsEnum } from '../api/api-reference';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SignalRService } from '../services/signalr.service';
import { SignalRBaseComponent } from '../services/signalr-base.component';
import { AuthService } from '../services/auth.service';
import { ConfirmDialogComponent } from '../modules/core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-firmware-update',
  standalone: true,
  imports: [TranslateModule, CommonModule, AngularSvgIconModule, ConfirmDialogComponent],
  templateUrl: './firmware-update.component.html',
  styleUrl: './firmware-update.component.scss'
})
export class FirmwareUpdateComponent extends SignalRBaseComponent implements OnChanges {
  @Input() firmwareStatus: FirmwareUpdateCommandStatusDataOut | null | undefined = null;
  @Output() closeSignal = new EventEmitter<boolean>();
  public PermissionsEnum = PermissionsEnum;
  readonly CommandStatus = CommandStatus;

  percentCompleted = 0;
  isCancelModalOpen = false;

  private readonly EVENT_NAME = 'firmware-image-update';

  constructor(
    private toastr: ToastrService,
    private client: Client,
    signalRService: SignalRService,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
  ) {
    super(signalRService);
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.setFirmwareStatus(this.firmwareStatus ?? null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['firmwareStatus']) {
      this.setFirmwareStatus(this.firmwareStatus ?? null);
    }
  }

  protected override async registerHandlers(): Promise<void> {
    this.registerSignalREvent(
      this.EVENT_NAME,
      (message: FirmwareUpdateCommandStatusDataOut) => {
        if (this.isDestroyed) return;
        if (message.meterNo === this.firmwareStatus?.meterNo) {
          this.setFirmwareStatus(message);
          this.cdr.detectChanges();
        }
      }
    );
  }

  private setFirmwareStatus(status: FirmwareUpdateCommandStatusDataOut | null): void {
    this.firmwareStatus = status;
    this.updateProgress();
  }

  private updateProgress(): void {
    if (!this.firmwareStatus) {
      this.percentCompleted = 0;
      return;
    }
    switch (this.firmwareStatus.status) {
      case CommandStatus.Pending:
        this.percentCompleted = 5;
        break;
      case CommandStatus.Failed:
      case CommandStatus.InProgress: {
        const stepsCount = this.firmwareStatus.steps?.length ?? 0;
        this.percentCompleted = Math.min(85, 10 + stepsCount * 15);
        break;
      }
      case CommandStatus.Finalize:
        this.percentCompleted = 90;
        break;
      case CommandStatus.Success:
        this.percentCompleted = 100;
        break;
      // For Canceled/ErrorOccurred: keep current value
    }
  }

  get isInProgress(): boolean {
    const s = this.firmwareStatus?.status;
    return s === CommandStatus.Pending || s === CommandStatus.InProgress || s === CommandStatus.Finalize;
  }

  get isFailed(): boolean {
    const s = this.firmwareStatus?.status;
    return s === CommandStatus.Failed || s === CommandStatus.ErrorOccured;
  }

  get targetModelName(): string {
    return this.firmwareStatus?.newMeterModelName ?? '';
  }

  toggleModal() {
    this.closeSignal.emit();
  }

  retryFirmwareUpdate() {
    if (!this.firmwareStatus?.id || this.isDestroyed || !this.authService.isAuthorized([PermissionsEnum.Device_firmware_update])) return;
    this.client.retryFirmwareUpdateCommands([this.firmwareStatus.id]).subscribe({
      next: (response) => {
        if (this.isDestroyed) return;
        this.toastr.success(response);
      },
      error: (err) => {
        if (this.isDestroyed) return;
        this.toastr.error(err);
      },
    });
  }

  abortFirmwareUpdate() {
    if (!this.firmwareStatus?.id || this.isDestroyed || !this.authService.isAuthorized([PermissionsEnum.Device_firmware_update])) return;

    this.client.abortFirmwareUpdateCommands([this.firmwareStatus.id]).subscribe({
      next: (response) => {
        if (this.isDestroyed) return;
        this.closeSignal.emit();
        this.toastr.success(response);
      },
      error: (err) => {
        if (this.isDestroyed) return;
        this.toastr.error(err);
      },
    });

    this.isCancelModalOpen = false;
  }
}
